import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductImages } from '../../../hooks/useProductImages';

type SeriesId = 'mercury' | 'heating' | 'uv';

type MercurySubId =
  | 'xenon-detection'
  | 'xenon-low-noise'
  | 'wee'
  | 'w350'
  | 'w500'
  | 'w1000'
  | 'w2000'
  | 'w5000'
  | 'ultra-high';

type HeatingSubId = 'rtp' | 'epi' | 'thin-film' | 'piw-spm';

type UVSubId = 'fluorescent' | 'alignment' | 'debond';

interface ProductRow {
  image?: string;
  model: string;
  oemModel: string;
  power: string;
  current: string;
  voltage: string;
  note?: string;
}

interface RTPProduct {
  image?: string;
  equipment: string;
  model: string;
  oemModel: string;
  voltage: string;
  power: string;
  colorTemp: string;
}

interface SimpleProduct {
  model: string;
  name: string;
  spec: string;
  app: string;
}

// ─── 汞氙灯（检测）────────────────────────────────────────────────────────
const xenonDetectionProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/dfc58a2a2da6c7331e10c5d7b189b5b0.png',
    model: 'NOP-150MO',
    oemModel: 'UXL-S150MO',
    power: '150W',
    current: '8.5A',
    voltage: '18V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/f8476422692cae23f206057ff33d442e.png',
    model: 'NOP-200KJQ',
    oemModel: 'L2570-01',
    power: '200W',
    current: '9.5A',
    voltage: '21V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/98b66ed4b2468ef2f317c33fe5236e90.png',
    model: 'NOP-230KJR',
    oemModel: 'L9588',
    power: '230W',
    current: '11A',
    voltage: '21V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/d7e8872d595cfc1a03461798c3eadcb6.png',
    model: 'NOP-350KJP',
    oemModel: 'L2917-10S',
    power: '350W',
    current: '13A',
    voltage: '27V',
  },

  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/6da98b779b58ff031e09f7c3c9d910e6.png',
    model: 'NOP-1002KT',
    oemModel: 'HBO1002KT',
    power: '1000W',
    current: '50A',
    voltage: '25V',
  },
];

// ─── 汞氙灯（低噪）────────────────────────────────────────────────────────
const xenonLowNoiseProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/de557ce66c19bdb8cf5db6e775a8212d.png',
    model: 'NOP-200KJL',
    oemModel: 'L12314',
    power: '200W',
    current: '8A',
    voltage: '24V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/8cb08116615a41418d9fdb3190852caf.png',
    model: 'NOP-200KJS',
    oemModel: 'L12755',
    power: '200W',
    current: '8A',
    voltage: '24V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/7492b4838055a19cf10812bcf0f2c409.png',
    model: 'NOP-200KJA',
    oemModel: 'L10852',
    power: '200W',
    current: '8A',
    voltage: '24V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/43417d9835c01f9f65c415f8fc724793.png',
    model: 'NOP-256BY',
    oemModel: 'USH-256BY',
    power: '250W',
    current: '10A',
    voltage: '23V',
  },
];

// ─── WEE ──────────────────────────────────────────────────────────────────
const weeProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/7152586e41304e179b81073164c700d4.png',
    model: 'NOP-250HS',
    oemModel: 'HBO-250W/HS',
    power: '250W',
    current: '6.5A',
    voltage: '40V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/c26f0e03df34f27192b92d3005822e66.png',
    model: 'NOP-250PP',
    oemModel: 'L2511T-HL',
    power: '250W',
    current: '6.5A',
    voltage: '40V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/379d590195f995634196a047afb55e9d.png',
    model: 'NOP-201HS',
    oemModel: 'HBO-201HS',
    power: '200W',
    current: '8A',
    voltage: '25V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/2659304f57feb091325e6c505686c349.png',
    model: 'NOP-255BY',
    oemModel: 'USH-255BY',
    power: '250W',
    current: '6.5A',
    voltage: '40V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/28f4b3a2e7079f78053c4a59f648db1d.png',
    model: 'NOP-G1000HA',
    oemModel: 'HR-1000GA',
    power: '750W',
    current: '17A',
    voltage: '44V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/82b0c1b3c86e147cfe64351efb44cc74.png',
    model: 'NOP-750H',
    oemModel: 'ORC-750H-AD',
    power: '750W',
    current: '16A',
    voltage: '47V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/99eeef070bd74ec1218937a8231f0610.png',
    model: 'NOP-1002NB',
    oemModel: 'TOSHIBA 1002NB',
    power: '850W',
    current: '17A',
    voltage: '50V',
  },
];

// ─── 350W ─────────────────────────────────────────────────────────────────
const w350Products: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/2ae70f122e622b443e597977bc6a783a.png',
    model: 'NOP-350J',
    oemModel: 'ABM-350J',
    power: '350W',
    current: '6A',
    voltage: '60V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/8979e1c6759cdd53b45a48e3fd5077bb.png',
    model: 'NOP-350AT',
    oemModel: 'USH-350AT',
    power: '350W',
    current: '6A',
    voltage: '60V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/d5ebc10e2380524056610f33e4448e55.png',
    model: 'NOP-350DS',
    oemModel: 'USH-350DS',
    power: '350W',
    current: '6A',
    voltage: '60V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/dec18466034308b792c38b7a861e4590.png',
    model: 'NOP-350WS',
    oemModel: 'HBO-350WS',
    power: '350W',
    current: '5.15A',
    voltage: '68V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/bc0304e45554472b4c148695a5c7f717.png',
    model: 'NOP-350DP',
    oemModel: 'USH-350DP',
    power: '350W',
    current: '6A',
    voltage: '60V',
  },
];

// ─── 500W ─────────────────────────────────────────────────────────────────
const w500Products: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/37483a74a494bf8d69e115499df153c4.png',
    model: 'NOP-500BY1',
    oemModel: 'USH-500BY1',
    power: '500W',
    current: '10A',
    voltage: '48V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/b11628fef09051ab2eec95b525c62266.png',
    model: 'NOP-500MB',
    oemModel: 'USH-500MB',
    power: '500W',
    current: '10A',
    voltage: '48V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/4d6738d2f7027c75e6aed7d7859168d1.png',
    model: 'NOP-500WA',
    oemModel: 'HBO-500WA',
    power: '500W',
    current: '7.3A',
    voltage: '60V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/11a95be57fdaca53cfc950d169b50d5e.png',
    model: 'NOP-501DK',
    oemModel: 'BML-501DK',
    power: '500W',
    current: '10A',
    voltage: '48V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e2a72687fc379ee8181f4c9253e54a56.png',
    model: 'NOP-500BY',
    oemModel: 'USH-500BY',
    power: '500W',
    current: '10A',
    voltage: '48V',
  },
];

// ─── 1000W ────────────────────────────────────────────────────────────────
const w1000Products: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/1af6e917696e939e331a28ab5fc05c36.png',
    model: 'NOP-1002NFL3',
    oemModel: 'USH-1002FNL3',
    power: '750W',
    current: '16A',
    voltage: '47V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/408dac637eed0147d52c4acbc6508175.png',
    model: 'NOP-1003PIL',
    oemModel: 'HBO-1003PIL',
    power: '750W',
    current: '27A',
    voltage: '26V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/06c264942e639cf72bdf37ea4b9621ef.png',
    model: 'NOP-1002NEL',
    oemModel: 'HBO-1002NEL',
    power: '750W',
    current: '16A',
    voltage: '47V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/c5127d7f8f8aefcfddb051d833d3b211.png',
    model: 'NOP-1000WD',
    oemModel: 'HBO-1000WD',
    power: '1000W',
    current: '26A',
    voltage: '37V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/91837af01e3071a7a603820a8a3cab2c.png',
    model: 'NOP-1002DF1',
    oemModel: 'WACOM1002DF1',
    power: '1000W',
    current: '12A',
    voltage: '80V',
  },
];

// ─── 2000W ────────────────────────────────────────────────────────────────
const w2000Products: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/71b0de35b2809974685c26fb30d16e5f.png',
    model: 'NOP-2001NIL',
    oemModel: 'USH-2001NIL',
    power: '1750W',
    current: '67A',
    voltage: '26V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e9150d29ca24f67f7e8668f1d94725e7.png',
    model: 'NOP-2002MAL',
    oemModel: 'WACOM-2002MAL',
    power: '2000W',
    current: '50A',
    voltage: '40V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/4c8e7ecdfe8a1cc1c7cb4e45cca873b2.png',
    model: 'NOP-2011NIL',
    oemModel: 'SUV/HBO-2011NIL',
    power: '2000W',
    current: '80A',
    voltage: '25V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/200bd298d3b26f741e7e67e4767d5b0e.png',
    model: 'NOP-2510NIL',
    oemModel: 'USH-2510NIHL',
    power: '2500W',
    current: '109A',
    voltage: '23V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/7dc7cdd5845db845ace9a92df2bee883.png',
    model: 'NOP-2701CIL',
    oemModel: 'USV-2701CIL',
    power: '2700W',
    current: '108A',
    voltage: '25V',
  },
];

// ─── 5000W ────────────────────────────────────────────────────────────────
const w5000Products: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/9ec95fc0327608214742ea9e8f8fbfa2.png',
    model: 'NOP-4500CIIL',
    oemModel: 'SUV-4500CIL',
    power: '4500W',
    current: '136A',
    voltage: '33V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/4a5212c7eddf5ba0bf1ce75938cb0a8a.png',
    model: 'NOP-5001NIL',
    oemModel: 'SUV-5001NIL',
    power: '5000W',
    current: '175.2A',
    voltage: '28.5V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/6a4ae617eadf3aedb3e98d17b0203013.png',
    model: 'NOP-5002TO',
    oemModel: 'WACOM-5002TO',
    power: '5000W',
    current: '69A',
    voltage: '72V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/20c4bff73c678d71841a1a528e92f4e4.png',
    model: 'NOP-5000CBT',
    oemModel: 'SUV-5000CBT',
    power: '5000W',
    current: '95A',
    voltage: '53V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/b2f52062b3764d2009184c6853e599f8.png',
    model: 'NOP-5000CSN',
    oemModel: 'SUV-5000CSN',
    power: '5000W',
    current: '95A',
    voltage: '53V',
  },
];

// ── 超高压 ───────────────────────────────────────────────────────────────
const ultraHighProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/4e3608a2974a10c268b296bff89e0566.png',
    model: 'NOP-8000CHL2',
    oemModel: 'USH-8000CHL2',
    power: '8000W',
    current: '105A',
    voltage: '75V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/99e8add608ee76e46d0936eb8fabf304.png',
    model: 'NOP-10003TA',
    oemModel: 'USH-10003TA',
    power: '10000W',
    current: '107A',
    voltage: '93V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e352583fc48361c42ffe94070ea682ae.png',
    model: 'NOP-12001',
    oemModel: 'UNL-12001',
    power: '12000W',
    current: '110A',
    voltage: '120V',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/a16e68c28f18111e3683cb2839cc084f.png',
    model: 'NOP-16005DE',
    oemModel: 'USH-16005DE',
    power: '16000W',
    current: '117A',
    voltage: '136V',
  },
];

// ─── 加热灯 RTP ───────────────────────────────────────────────────────────
const rtpProducts: RTPProduct[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/544d07f60900983aca7b6ca43728d2a5.png',
    equipment: 'AMAT-XE',
    model: 'PX-82V-500W',
    oemModel: '0190-36349',
    voltage: '82V',
    power: '500W',
    colorTemp: '2950K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/1e2b607fd9f726f81873b406adb5deba.png',
    equipment: 'AMAT-MOD I',
    model: 'PX-120V-750W',
    oemModel: '0010-54227',
    voltage: '120V',
    power: '750W',
    colorTemp: '2950K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/fc729432f426f9c169141a2bf525ee9d.png',
    equipment: 'AMAT-Vantag Radiance',
    model: 'PX-82V-540W',
    oemModel: '0190-38516',
    voltage: '82V',
    power: '540W',
    colorTemp: '2950K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/defbd7f61adf34e57e1f1e280e0bad12.png',
    equipment: 'AMAT-Vantage Radiance PLS',
    model: 'PX-95V-645W',
    oemModel: '0190-38517',
    voltage: '95V',
    power: '645W',
    colorTemp: '2850K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/0047997061a7dbd5dc7c2c6bacd954e5.png',
    equipment: 'Mattson-AST',
    model: 'PX-240V-1500W',
    oemModel: '1010119',
    voltage: '240V',
    power: '1500W',
    colorTemp: '2400K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/463667440277d16cb374ca2058960463.png',
    equipment: 'Mattson-AST',
    model: 'PX-230V-2000W',
    oemModel: '1010118-1',
    voltage: '230V',
    power: '2000W',
    colorTemp: '3000K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/0047997061a7dbd5dc7c2c6bacd954e5.png',
    equipment: 'Mattson-Helios III',
    model: 'PX-480V-3700W',
    oemModel: '1010523',
    voltage: '480V',
    power: '3700W',
    colorTemp: '2850K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/463667440277d16cb374ca2058960463.png',
    equipment: 'Mattson-Helios III',
    model: 'PX-480V-6000W',
    oemModel: '1012728',
    voltage: '480V',
    power: '6000W',
    colorTemp: '3050K',
  },
];

// ─── 加热灯 EPI ───────────────────────────────────────────────────────────
const epiProducts: RTPProduct[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/35c80ac1a5c9476603b97b7923d10ebd.png',
    equipment: 'AMAT-Centura(8inch)',
    model: 'PX-120V-2000W',
    oemModel: '0190-22509 / 0190-88173',
    voltage: '120V',
    power: '2000W',
    colorTemp: '2950K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/35c80ac1a5c9476603b97b7923d10ebd.png',
    equipment: 'AMAT-Centura ATM(12inch)',
    model: 'PX-120V-2000W',
    oemModel: '0190-88173',
    voltage: '120V',
    power: '2000W',
    colorTemp: '3050K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/121ef56b8f79bdcf7a14cb9169cab5c7.png',
    equipment: 'ASM-Epsilon(8inch)',
    model: 'PX-480V-6KWL303',
    oemModel: '1002018',
    voltage: '480V',
    power: '6000W',
    colorTemp: '3000K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/121ef56b8f79bdcf7a14cb9169cab5c7.png',
    equipment: 'ASM-Epsilon(8inch)',
    model: 'PX-480V-6KWL350',
    oemModel: '1002018',
    voltage: '480V',
    power: '6000W',
    colorTemp: '3050K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/121ef56b8f79bdcf7a14cb9169cab5c7.png',
    equipment: 'ASM-Epsilon(8inch)',
    model: 'PX-480V-6.6KWL350',
    oemModel: '1002018',
    voltage: '480V',
    power: '6600W',
    colorTemp: '3050K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/9cafb2ef94a7eaa6967f25a9943dd383.png',
    equipment: 'ASM-Intrepid ESA(12inch)',
    model: 'PX-480V-10KWL450',
    oemModel: '1223-977-01',
    voltage: '480V',
    power: '10000W',
    colorTemp: '3050K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/9cafb2ef94a7eaa6967f25a9943dd383.png',
    equipment: 'ASM-Intrepid ESA(12inch)',
    model: 'PX-480V-10KWL490',
    oemModel: '1195-374-01',
    voltage: '480V',
    power: '10000W',
    colorTemp: '3050K',
  },
];

// ─── 加热灯 Thin Film ─────────────────────────────────────────────────────
const thinFilmProducts: RTPProduct[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/a987abbe7ada0bbaff09a368a49a6b44.png',
    equipment: 'AMAT-PVD',
    model: 'PX-120V-500W',
    oemModel: '1010-01254',
    voltage: '120V',
    power: '500W',
    colorTemp: '2450K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/fa7d86357f0b95d328c2383f76b44e7f.png',
    equipment: 'AMAT-P5000',
    model: 'PX-120V-1000W',
    oemModel: '1010-01088',
    voltage: '120V',
    power: '1000W',
    colorTemp: '2950K',
  },
];

// ─── 加热灯 PIW & SPM ─────────────────────────────────────────────────────
const piwSpmProducts: RTPProduct[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/0dc85fc78422be760e83bb5627af422c.png',
    equipment: 'PIW-Heater lamp',
    model: 'PX-220V-1350W',
    oemModel: '',
    voltage: '220V',
    power: '1350W',
    colorTemp: '2500K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/8dee6202f9427091b78d6476be249d8f.png',
    equipment: 'PIW-Heater lamp',
    model: 'PX-208V-3190W',
    oemModel: '',
    voltage: '208V',
    power: '3190W',
    colorTemp: '2580K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/d27d7a566588cbcef8d30476db9acf09.png',
    equipment: 'PIW-Heater lamp',
    model: 'PX-208V-2000W',
    oemModel: '',
    voltage: '208V',
    power: '2000W',
    colorTemp: '2580K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/0c2896fb8c3d7aca9550479bc643d0e0.png',
    equipment: 'PIW-Heater lamp',
    model: 'PX-208V-4000W',
    oemModel: '',
    voltage: '208V',
    power: '4000W',
    colorTemp: '2450K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/df85ce8e4742a8fe9a0527ad1e7e7e3f.png',
    equipment: 'SPM-Heater lamp',
    model: 'PX-380V-3000W',
    oemModel: '',
    voltage: '380V',
    power: '3000W',
    colorTemp: '2550K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/5c6535d29d090c36394a1914c8d5cce9.png',
    equipment: 'SPM-Heater lamp',
    model: 'PX-220V-1500W',
    oemModel: '',
    voltage: '220V',
    power: '1500W',
    colorTemp: '2500K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/d6e7a0181d10c73d4ecf7c5a658a4ef4.png',
    equipment: 'SPM-Heater lamp',
    model: 'PX-220V-2000W',
    oemModel: '',
    voltage: '220V',
    power: '2000W',
    colorTemp: '2500K',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/9379b4d0bfa3405129de8787d68b41ce.png',
    equipment: 'SPM-Heater lamp',
    model: 'PX-220V-4000W',
    oemModel: '',
    voltage: '220V',
    power: '4000W',
    colorTemp: '2500K',
  },
];

interface UVFluorescentProduct {
  image?: string;
  model: string;
  oemModel: string;
  voltage: string;
  power: string;
  length: string;
}

// ─── 荧光UV灯 ─────────────────────────────────────────────────────────────
const fluorescentUVProducts: UVFluorescentProduct[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e9c9e1c432ef16b4716c704ccc961f56.png',
    model: 'PX-FLR-T12UV32-L2400',
    oemModel: 'TOSHIBAFLR110H-UV32/A',
    voltage: '220V',
    power: '110W',
    length: '2.4M',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e9c9e1c432ef16b4716c704ccc961f56.png',
    model: 'PX-FLR-T12UV32-L2400',
    oemModel: 'Unilam LUV/F-11QW/UVB',
    voltage: '220V',
    power: '45W',
    length: '1.7M',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/e9c9e1c432ef16b4716c704ccc961f56.png',
    model: 'PX-FLR-T5UV32-L1700C',
    oemModel: 'TOSHIBAFLR1700T5UV32A',
    voltage: '220V',
    power: '28W',
    length: '1.1M',
  },
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/06cc3d5dc2bc89e48166460df470fb84.png',
    model: 'PX-PLL36WBLB-UVA',
    oemModel: 'PLL36WBLB-UVA',
    voltage: '106V',
    power: '36W',
    length: '0.6M',
  },
];

// ─── 配向灯 ───────────────────────────────────────────────────────────────
const alignmentProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/426c99d158bb45b5166d4287e5eb5390.png',
    model: 'H101508-4KW',
    oemModel: '5128X/H',
    voltage: '330V',
    power: '4000W',
    current: '12A',
  },
];

// ─── 解胶灯 ───────────────────────────────────────────────────────────────
const debondProducts: ProductRow[] = [
  {
    image: 'https://static.readdy.ai/image/f6265e0f35e672632603158b994d4cb8/6bf7ef84eb6ad6ce44af75cf56ccce9a.png',
    model: '4KW-0550M',
    oemModel: 'ORC-0550M',
    voltage: '490V',
    power: '4000W',
    current: '7.3A',
  },
];

const UV_SUB_TABS: { id: UVSubId; label: string; count: number }[] = [
  { id: 'fluorescent', label: 'Fluorescent', count: fluorescentUVProducts.length },
  { id: 'alignment', label: 'Alignment', count: alignmentProducts.length },
  { id: 'debond', label: 'Degumming', count: debondProducts.length },
];

const HEATING_SUB_TABS: { id: HeatingSubId; label: string; count: number }[] = [
  { id: 'rtp', label: 'RTP', count: rtpProducts.length },
  { id: 'epi', label: 'EPI', count: epiProducts.length },
  { id: 'thin-film', label: 'Thin Film', count: thinFilmProducts.length },
  { id: 'piw-spm', label: 'PIW & SPM', count: piwSpmProducts.length },
];

// ──────────────────────────────────────────────────────────────────────────

const SERIES_TABS: { id: SeriesId; nameKey: string; icon: string }[] = [
  { id: 'mercury', nameKey: 'products.series.mercury.name', icon: 'ri-contrast-drop-2-line' },
  { id: 'heating', nameKey: 'products.series.heating.name', icon: 'ri-temp-hot-line' },
  { id: 'uv', nameKey: 'products.series.uv.name', icon: 'ri-sun-line' },
];

const MERCURY_SUB_TABS: { id: MercurySubId; label: string; products: ProductRow[] }[] = [
  { id: 'xenon-detection', label: 'Xeno-inspec', products: xenonDetectionProducts },
  { id: 'xenon-low-noise', label: 'Xeno-low noise', products: xenonLowNoiseProducts },
  { id: 'wee', label: 'WEE', products: weeProducts },
  { id: 'w350', label: '350W', products: w350Products },
  { id: 'w500', label: '500W', products: w500Products },
  { id: 'w1000', label: '1000W', products: w1000Products },
  { id: 'w2000', label: '2000W', products: w2000Products },
  { id: 'w5000', label: '5000W', products: w5000Products },
  { id: 'ultra-high', label: 'SHP', products: ultraHighProducts },
];

const SERIES_ACCENT = {
  mercury: { hover: 'hover:bg-amber-50/60', badge: 'bg-amber-50 text-amber-700' },
  heating: { hover: 'hover:bg-orange-50/60', badge: 'bg-orange-50 text-orange-700' },
  uv: { hover: 'hover:bg-rose-50/60', badge: 'bg-rose-50 text-rose-700' },
};

interface SpecTableProps {
  products: ProductRow[];
  badgeClass: string;
  editable?: boolean;
  imageUrls?: Record<string, string>;
  onImageChange?: (model: string, file: File) => void;
}

function SpecTable({ products, badgeClass, editable, imageUrls, onImageChange }: SpecTableProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingModelRef = useRef<string | null>(null);

  const handleImageCellClick = useCallback((model: string) => {
    if (!editable) return;
    editingModelRef.current = model;
    fileInputRef.current?.click();
  }, [editable]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingModelRef.current) return;
    onImageChange?.(editingModelRef.current, file);
    editingModelRef.current = null;
    e.target.value = '';
  }, [onImageChange]);

  if (products.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-16 flex flex-col items-center justify-center gap-3">
        <i className="ri-inbox-line text-3xl text-gray-300" />
        <p className="text-sm text-gray-400">该子系列产品数据待完善</p>
      </div>
    );
  }

  return (
    <>
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="bg-[#EBF0F8] border-b border-[#1B3F7A]/20">
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider w-44">{t('table.image')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.onopto_model')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.oem_model')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.power')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.current')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.voltage')}</th>
              <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.note')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, idx) => {
              const displayImage = imageUrls?.[product.model] || product.image;
              return (
                <tr
                  key={`${product.model}-${idx}`}
                  className={`border-b border-gray-100 hover:bg-amber-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                >
                  <td className="py-3 px-4 text-center">
                    <div
                      className={`w-36 h-[68px] flex items-center justify-center mx-auto relative group ${editable ? 'cursor-pointer' : ''}`}
                      onClick={() => handleImageCellClick(product.model)}
                      title={editable ? '点击上传产品图片' : undefined}
                    >
                      {displayImage ? (
                        <>
                          <img
                            src={displayImage}
                            alt={product.model}
                            className="w-full h-full object-contain"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                          {editable && (
                            <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                              <i className="ri-image-edit-line text-white text-lg" />
                              <span className="text-white text-[10px] font-medium whitespace-nowrap">点击替换</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={`w-full h-full rounded-lg flex flex-col items-center justify-center gap-1 transition-colors ${editable ? 'bg-amber-50 border-2 border-dashed border-amber-200 group-hover:border-amber-400 group-hover:bg-amber-100/60' : 'bg-gray-100'}`}>
                          <i className={`text-xl ${editable ? 'ri-upload-cloud-2-line text-amber-400 group-hover:text-amber-500' : 'ri-image-line text-gray-300'}`} />
                          {editable && <span className="text-[10px] text-amber-400 group-hover:text-amber-500 font-medium whitespace-nowrap">上传图片</span>}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    <span className="font-bold text-gray-900 text-sm font-mono">{product.model}</span>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    <span className="text-gray-500 text-sm font-mono">{product.oemModel}</span>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    <span className={`inline-block font-bold text-sm px-3 py-1 rounded-full ${badgeClass}`}>{product.power}</span>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    <span className="text-gray-700 font-semibold text-sm">{product.current}</span>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    <span className="text-gray-700 font-semibold text-sm">{product.voltage}</span>
                  </td>
                  <td className="py-3 px-4 text-center whitespace-nowrap">
                    {product.note ? (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{product.note}</span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface RTPTableProps {
  products: RTPProduct[];
  badgeClass: string;
}

function RTPTable({ products, badgeClass }: RTPTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr className="bg-[#EBF0F8] border-b border-[#1B3F7A]/20">
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider w-36">{t('table.image')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.equipment')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.onopto_model')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.oem_model')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.voltage')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.power')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.color_temp')}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, idx) => (
            <tr
              key={`${product.model}-${idx}`}
              className={`border-b border-gray-100 hover:bg-orange-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
            >
              <td className="py-3 px-4 text-center">
                <div className="w-28 h-16 flex items-center justify-center mx-auto">
                  {product.image ? (
                    <img src={product.image} alt={product.model} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                      <i className="ri-image-line text-gray-300 text-xl" />
                    </div>
                  )}
                </div>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-700 text-sm font-medium">{product.equipment}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="font-bold text-gray-900 text-sm font-mono">{product.model}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-500 text-sm font-mono">{product.oemModel}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-700 font-semibold text-sm">{product.voltage}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className={`inline-block font-bold text-sm px-3 py-1 rounded-full ${badgeClass}`}>{product.power}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-600 text-sm font-semibold">{product.colorTemp}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface UVFluorescentTableProps {
  products: UVFluorescentProduct[];
  badgeClass: string;
}

function UVFluorescentTable({ products, badgeClass }: UVFluorescentTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full min-w-[860px]">
        <thead>
          <tr className="bg-[#EBF0F8] border-b border-[#1B3F7A]/20">
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider w-44">{t('table.image')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.onopto_model')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.oem_model')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.voltage')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.power')}</th>
            <th className="text-center py-3.5 px-4 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.length')}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, idx) => (
            <tr
              key={`${product.model}-${product.oemModel}`}
              className={`border-b border-gray-100 hover:bg-rose-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
            >
              <td className="py-3 px-4 text-center">
                <div className="w-36 h-[68px] flex items-center justify-center mx-auto">
                  {product.image ? (
                    <img src={product.image} alt={product.model} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                      <i className="ri-image-line text-gray-300 text-xl" />
                    </div>
                  )}
                </div>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="font-bold text-gray-900 text-sm font-mono">{product.model}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-500 text-sm font-mono">{product.oemModel}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-700 font-semibold text-sm">{product.voltage}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className={`inline-block font-bold text-sm px-3 py-1 rounded-full ${badgeClass}`}>{product.power}</span>
              </td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <span className="text-gray-600 text-sm font-semibold">{product.length}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SimpleTableProps {
  products: SimpleProduct[];
  badgeClass: string;
  hoverClass: string;
}

function SimpleTable({ products, badgeClass, hoverClass }: SimpleTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full min-w-[700px]">
        <thead>
          <tr className="bg-[#EBF0F8] border-b border-[#1B3F7A]/20">
            <th className="text-center py-3.5 px-5 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.onopto_model')}</th>
            <th className="text-center py-3.5 px-5 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider">{t('table.name')}</th>
            <th className="text-center py-3.5 px-5 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider whitespace-nowrap">{t('table.spec')}</th>
            <th className="text-center py-3.5 px-5 text-xs font-semibold text-[#1B3F7A] uppercase tracking-wider">{t('table.app')}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, idx) => (
            <tr
              key={p.model}
              className={`border-b border-gray-100 transition-colors ${hoverClass} ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
            >
              <td className="py-4 px-5 text-center whitespace-nowrap">
                <span className="font-bold text-gray-900 text-sm font-mono">{p.model}</span>
              </td>
              <td className="py-4 px-5 text-center">
                <span className="text-gray-800 text-sm font-medium">{p.name}</span>
              </td>
              <td className="py-4 px-5 text-center whitespace-nowrap">
                <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${badgeClass}`}>{p.spec}</span>
              </td>
              <td className="py-4 px-5 text-center">
                <span className="text-gray-500 text-sm leading-relaxed">{p.app}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SeriesSection() {
  const { t } = useTranslation();
  const [activeSeries, setActiveSeries] = useState<SeriesId>('mercury');
  const [mercurySubId, setMercurySubId] = useState<MercurySubId>('xenon-detection');
  const [heatingSubId, setHeatingSubId] = useState<HeatingSubId>('rtp');
  const [uvSubId, setUVSubId] = useState<UVSubId>('fluorescent');

  const { imageUrls: mercuryImageUrls, saveImage: saveMercuryImage } = useProductImages('mercury');

  const EDITABLE_MERCURY_SUBS: MercurySubId[] = ['xenon-low-noise', 'wee', 'w350', 'w500', 'w1000', 'w2000'];
  const isMercuryEditable = EDITABLE_MERCURY_SUBS.includes(mercurySubId);

  const handleMercuryImageChange = useCallback((model: string, file: File) => {
    saveMercuryImage(model, file);
  }, [saveMercuryImage]);

  const accent = SERIES_ACCENT[activeSeries];

  const currentSub = MERCURY_SUB_TABS.find(s => s.id === mercurySubId)!;

  const heatingProducts: SimpleProduct[] = [
    { model: t('products.series.heating.p1.model'), name: t('products.series.heating.p1.name'), spec: t('products.series.heating.p1.spec'), app: t('products.series.heating.p1.app') },
    { model: t('products.series.heating.p2.model'), name: t('products.series.heating.p2.name'), spec: t('products.series.heating.p2.spec'), app: t('products.series.heating.p2.app') },
    { model: t('products.series.heating.p3.model'), name: t('products.series.heating.p3.name'), spec: t('products.series.heating.p3.spec'), app: t('products.series.heating.p3.app') },
    { model: t('products.series.heating.p4.model'), name: t('products.series.heating.p4.name'), spec: t('products.series.heating.p4.spec'), app: t('products.series.heating.p4.app') },
  ];


  return (
    <section className="pt-8 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Main Series Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {SERIES_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSeries(tab.id)}
              className={`flex items-center gap-3 px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeSeries === tab.id
                  ? 'bg-[#1B3F7A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${tab.icon} text-lg`} />
              {t(tab.nameKey)}
            </button>
          ))}
        </div>

        {/* Mercury Series */}
        {activeSeries === 'mercury' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-black text-gray-900 mb-2">{t('products.series.mercury.name')}</h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-3xl">{t('products.series.mercury.desc')}</p>
            </div>

            {/* Sub-series Tab Bar */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {MERCURY_SUB_TABS.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setMercurySubId(sub.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    mercurySubId === sub.id
                      ? 'bg-amber-100 text-amber-800 font-semibold'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {sub.label}
                  {sub.products.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                      mercurySubId === sub.id ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {sub.products.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Count hint */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-400">
                {currentSub.products.length > 0 ? (
                  <>共 <span className="font-bold text-gray-600">{currentSub.products.length}</span> 款产品</>
                ) : '暂无产品数据'}
              </p>
              <div className="flex items-center gap-3">
                {isMercuryEditable && (
                  <span className="flex items-center gap-1 text-xs text-amber-500 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100">
                    <i className="ri-edit-box-line" />
                    点击产品图可上传替换
                  </span>
                )}
                <p className="text-xs text-gray-400">{t('table.custom')}</p>
              </div>
            </div>

            <SpecTable
              products={currentSub.products}
              badgeClass={accent.badge}
              editable={isMercuryEditable}
              imageUrls={mercuryImageUrls}
              onImageChange={handleMercuryImageChange}
            />
          </div>
        )}

        {/* Heating Series */}
        {activeSeries === 'heating' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-black text-gray-900 mb-2">{t('products.series.heating.name')}</h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-3xl">{t('products.series.heating.desc')}</p>
            </div>

            {/* Heating Sub-series Tab Bar */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {HEATING_SUB_TABS.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setHeatingSubId(sub.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    heatingSubId === sub.id
                      ? 'bg-orange-100 text-orange-800 font-semibold'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {sub.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    heatingSubId === sub.id ? 'bg-orange-200 text-orange-700' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {sub.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Count hint */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-400">
                共 <span className="font-bold text-gray-600">
                  {heatingSubId === 'rtp' ? rtpProducts.length
                    : heatingSubId === 'epi' ? epiProducts.length
                    : heatingSubId === 'thin-film' ? thinFilmProducts.length
                    : piwSpmProducts.length}
                </span> 款产品
              </p>
              <p className="text-xs text-gray-400">{t('table.custom')}</p>
            </div>

            {heatingSubId === 'rtp' && (
              <RTPTable products={rtpProducts} badgeClass={accent.badge} />
            )}
            {heatingSubId === 'epi' && (
              <RTPTable products={epiProducts} badgeClass={accent.badge} />
            )}
            {heatingSubId === 'thin-film' && (
              <RTPTable products={thinFilmProducts} badgeClass={accent.badge} />
            )}
            {heatingSubId === 'piw-spm' && (
              <RTPTable products={piwSpmProducts} badgeClass={accent.badge} />
            )}
          </div>
        )}

        {/* UV Series */}
        {activeSeries === 'uv' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-black text-gray-900 mb-2">{t('products.series.uv.name')}</h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-3xl">{t('products.series.uv.desc')}</p>
            </div>

            {/* UV Sub-series Tab Bar */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {UV_SUB_TABS.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setUVSubId(sub.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    uvSubId === sub.id
                      ? 'bg-rose-100 text-rose-800 font-semibold'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {sub.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                    uvSubId === sub.id ? 'bg-rose-200 text-rose-700' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {sub.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Count hint */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-400">
                共 <span className="font-bold text-gray-600">
                  {uvSubId === 'fluorescent' ? fluorescentUVProducts.length
                    : uvSubId === 'alignment' ? alignmentProducts.length
                    : debondProducts.length}
                </span> 款产品
              </p>
              <p className="text-xs text-gray-400">{t('table.custom')}</p>
            </div>

            {uvSubId === 'fluorescent' && (
              <UVFluorescentTable products={fluorescentUVProducts} badgeClass={accent.badge} />
            )}
            {uvSubId === 'alignment' && (
              <SpecTable products={alignmentProducts} badgeClass={accent.badge} />
            )}
            {uvSubId === 'debond' && (
              <SpecTable products={debondProducts} badgeClass={accent.badge} />
            )}
          </div>
        )}

      </div>
    </section>
  );
}
